import {
  useEffect,
  useMemo,
  useRef,
} from "react";
import { IOrganisationResponseItem } from "shared";

import { IOption, MultiSelect } from "../../../common/Atoms/MultiSelect";
import { useOrganisationsIncludeDisconnected } from "../../hooks/useOrganisationsIncludeDisconnected";
import { getLastSelectedOrgId } from "../../lib/lastSelectedOrg";

interface MultiOrgSelectorProps {
  onChange: (value: string[]) => void;
  selected: string[];
}

export function MultiOrgSelector({
  onChange,
  selected,
}: MultiOrgSelectorProps) {
  const { data: orgs } = useOrganisationsIncludeDisconnected();

  // Default to the most recently viewed org (this tab) if there is one.
  // Ref-guarded so clearing the selection later doesn't trigger a re-select.
  const didAutoSelect = useRef(false);

  useEffect(() => {
    if (didAutoSelect.current) return;
    if (selected.length > 0) {
      didAutoSelect.current = true;

      return;
    }
    const lastOrgId = getLastSelectedOrgId();
    const lastOrg = lastOrgId ? orgs.find(o => o.id === lastOrgId && o.connected) : undefined;
    if (lastOrg) {
      didAutoSelect.current = true;
      onChange([lastOrg.id]);
    }
  }, [orgs, selected.length, onChange]);

  const orgOptions: IOption[] = useMemo(() => {
    return orgs.map(org => {
      const isAllowedMulti = allowedMulti(org, selected, orgs);

      return {
        label: org.legalName + (org.connected ? `` : ` (Disconnected)`),
        value: org.id,
        disabled: !org.connected || !isAllowedMulti,
        tooltip: isAllowedMulti ? undefined : `Only organisations with "Pro" plan can be combined in reports. Remove other selections first.`,
        badge: org.allowedInMultiEntity ? `Pro` : undefined,
        badgeColor: `blue`,
      };
    });
  }, [ orgs ]);

  function renderDisplay() {
    if (selected.length === 0) {
      return `Select Organisation(s)`;
    }

    if (selected.length <= 3) {
      return orgOptions.filter(i => selected.includes(i.value as string)).map(i => i.label).join(`, `);
    }

    return `${orgOptions.find(i => i.value === selected[0])?.label} + ${selected.length - 1} more`;
  }

  return (
    <MultiSelect
      onChange={ onChange }
      options={ orgOptions }
      selected={ selected }
      display={ renderDisplay() }
      clearable
    />
  );
}

function allowedMulti(org: IOrganisationResponseItem, selected: string[], orgs: IOrganisationResponseItem[]) {
  const otherSelected = selected.filter(i => i !== org.id);

  if (!org.allowedInMultiEntity && otherSelected.length > 0) {
    return false;
  }

  const otherOrgs = orgs.filter(i => otherSelected.includes(i.id));

  if (otherOrgs.find(i => !i.allowedInMultiEntity)) {
    return false;
  }

  return true;
}
